import React, { useEffect, useState } from 'react';
import { locale } from '../../common/localization/localizationService';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { FilterOption } from '../orderOverview/OrderOverview';
import moment from 'moment';
import { IFraction } from '../../models/Fraction.model';
import Select from 'react-select';
import { ISideFilter } from '../../models/SideFilterObject.model';

export interface ISideFilterProps {
  filter: ISideFilter;
  orderActions: any;
  allFractions: Array<IFraction>;
  statuses: Array<FilterOption>;
  routes: Array<FilterOption>;
  fractionOptions: Array<FilterOption>;
  contractors: Array<FilterOption>;
  filterAction: any;
  filterByInerval: (fromDate: string, toDate: string) => void;
}

export default function SideFilter(props: ISideFilterProps): JSX.Element {
  const [isFilterInvalid, setFilterValidity] = useState<boolean>(false);

  const getDefaultContractors = () => {
    if (props.contractors) {
      if (props.contractors.length < 2) {
        return [props.contractors[0]];
      } else {
        return props.contractors.filter((contractor: any) =>
          props.filter.contractorIds.includes(contractor.value),
        );
      }
    } else {
      return [];
    }
  };

  const defaultStatuses = props.statuses
    ? props.statuses.filter((status: any) => {
        return props.filter.statusIds.includes(status.value);
      })
    : [];

  const defaultRoutes = props.routes
    ? props.routes.filter((route) => {
        return props.filter.routeIds.includes(route.label);
      })
    : [];

  const defaultFractions = props.fractionOptions
    ? props.fractionOptions.filter((fraction) =>
        props.filter.fractionIds.includes(fraction.value as number),
      )
    : [];

  const defaultContractors = getDefaultContractors();

  // Server side rendering on date interval change
  useEffect(() => {
    checkDateValidity(props.filter.fromDate, props.filter.toDate);
    if (!isFilterInvalid) {
      filterOrders();
    }
  }, [props.filter.fromDate, props.filter.toDate]);

  const filterOrders = () => {
    if (props.filter.fromDate && props.filter.toDate) {
      const formattedFromDate = new Date(props.filter.fromDate)
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '');
      const formattedToDate = new Date(props.filter.toDate)
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '');
      props.filterByInerval(formattedFromDate, formattedToDate);
    }
  };

  const handleFromDateChange = (event: any) => {
    checkDateValidity(event.value, props.filter.toDate);
    props.filterAction({
      ...props.filter,
      fromDate: event.value,
    });
  };

  const handleToDateChange = (event: any) => {
    checkDateValidity(props.filter.fromDate, event.value);
    props.filterAction({
      ...props.filter,
      toDate: event.value,
    });
  };

  const checkDateValidity = (from: Date, to: Date): void => {
    if (moment(from).unix() > moment(to).unix()) {
      setFilterValidity(true);
    } else {
      setFilterValidity(false);
    }
  };

  const filterByStatus = (selectedOptions: Array<FilterOption>) => {
    if (selectedOptions) {
      const selectedIds = selectedOptions.map(
        (option: FilterOption) => option.value,
      );
      props.filterAction({
        ...props.filter,
        statusIds: selectedIds,
      });
    }
  };

  const filterByRoute = (selectedOptions: Array<FilterOption>) => {
    if (selectedOptions) {
      const selectedRoutes = selectedOptions.map(
        (option: FilterOption) => option.value,
      );
      props.filterAction({
        ...props.filter,
        routeIds: selectedRoutes,
      });
    }
  };

  const filterByFraction = (selectedOptions: Array<FilterOption>) => {
    if (selectedOptions) {
      const selectedIds = selectedOptions
        .map((option: FilterOption) => option.value)
        .sort((a: number, b: number) => {
          return a - b;
        });
      props.filterAction({
        ...props.filter,
        fractionIds: selectedIds,
      });
    }
  };

  const filterByContractor = (selectedOptions: Array<FilterOption>) => {
    if (selectedOptions) {
      const selectedIds = selectedOptions.map(
        (option: FilterOption) => option.value,
      );
      props.filterAction({
        ...props.filter,
        contractorIds: selectedIds,
      });
    }
  };

  return (
    <>
      <div className="filter-column">
        <label>{locale.orderOverview._from}</label>
        <DatePicker
          value={new Date(props.filter.fromDate)}
          onChange={handleFromDateChange}
          format={locale.general._fullDateFormat}
          className={`date-picker ${isFilterInvalid ? 'input-invalid' : ''}`}
          popupSettings={{ popupClass: 'date-picker-popup' }}
        />
        <label>{locale.orderOverview._to}</label>
        <DatePicker
          value={new Date(props.filter.toDate)}
          onChange={handleToDateChange}
          format={locale.general._fullDateFormat}
          className={`date-picker ${isFilterInvalid ? 'input-invalid' : ''}`}
          popupSettings={{ popupClass: 'date-picker-popup' }}
        />
        {props.statuses && (
          <>
            <label>{locale.orderOverview._status}</label>
            <Select
              options={props.statuses}
              value={defaultStatuses}
              onChange={(e: Array<FilterOption>) => filterByStatus(e)}
              isMulti
              isDisabled={
                props.statuses.length < 2 && defaultStatuses.length < 1
              }
              placeholder={locale.orders._chooseStatus}
              className="input-field react-select"
            />
          </>
        )}
        {props.routes && (
          <>
            <label>{locale.orderOverview._routes}</label>
            <Select
              options={props.routes}
              value={defaultRoutes}
              onChange={(e: Array<FilterOption>) => filterByRoute(e)}
              isMulti
              isDisabled={props.routes.length < 2 && defaultRoutes.length < 1}
              placeholder={locale.orderOverview._chooseRoutes}
              className="input-field react-select"
            />
          </>
        )}
      </div>
      <div className="filter-column">
        {props.fractionOptions && (
          <>
            <label>{locale.orderOverview._fractions}</label>
            <Select
              options={props.fractionOptions}
              value={defaultFractions}
              onChange={(e: Array<FilterOption>) => filterByFraction(e)}
              isMulti
              isDisabled={props.allFractions.length < 2}
              placeholder={locale.orders._chooseFraction}
              className="input-field react-select"
            />
          </>
        )}
        {props.contractors && (
          <>
            <label>{locale.orderOverview._contractors}</label>
            <Select
              options={props.contractors}
              value={defaultContractors}
              onChange={(e: Array<FilterOption>) => filterByContractor(e)}
              isMulti
              isDisabled={props.contractors.length < 2}
              placeholder={locale.orders._chooseContractor}
              className="input-field react-select"
              menuPlacement="auto"
            />
          </>
        )}
        <div className="filter-button-wrapper">
          {isFilterInvalid && (
            <div className="filter-invalid">
              {locale.orderOverview._dateFilterInvalid}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
